/**
 * Equipment lifecycle overrides (EQUIP-LIFECYCLE-02) — the office's authoritative
 * say on whether an ESC unit is active or removed, keyed to the ESC counter.
 * Pegasus-native (never writes ESC, DEC-22). Beats the derived replaced-guess in
 * buildEquipmentView, and lets the office record a warranty/breakdown/upgrade
 * removal that ESC only ever captured as a free-text note.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { EquipOverride } from "@/lib/equipment";

export type RemovalReason = "warranty" | "breakdown" | "upgrade" | "other";
export const REASON_LABEL: Record<RemovalReason, string> = {
  warranty: "Warranty replacement",
  breakdown: "Breakdown / failure",
  upgrade: "Upgrade / replacement",
  other: "Other",
};

export function useEquipmentOverrides(custno: string | undefined, locno?: string | null) {
  return useQuery({
    queryKey: ["equip_overrides", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Map<string, EquipOverride>> => {
      let q = supabase
        .from("pegasus_equipment_override")
        .select("esc_counter, status, removal_date, removal_reason, replaced_by_model, mfg, model, serial, install_date, eq_type, on_plan, equip_loc")
        .eq("custno", custno as string);
      if (locno) q = q.eq("locno", locno);
      const { data, error } = await q;
      if (error) throw error;
      const m = new Map<string, EquipOverride>();
      for (const r of (data ?? []) as EquipOverride[]) m.set(r.esc_counter, r);
      return m;
    },
  });
}

export function useSetEquipmentOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      esc_counter: string; custno: string; locno?: string | null;
      status: "removed" | "active";
      removal_reason?: RemovalReason | null;
      removal_date?: string | null;
      note?: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_equipment_override").upsert({
        esc_counter: v.esc_counter, custno: v.custno, locno: v.locno ?? null,
        status: v.status,
        removal_reason: v.status === "removed" ? (v.removal_reason ?? "other") : null,
        removal_date: v.status === "removed" ? (v.removal_date ?? new Date().toISOString().slice(0, 10)) : null,
        note: v.note ?? null,
        set_by: u?.user?.email ?? null,
        set_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["equip_overrides", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer_equipment", v.custno] });
    },
  });
}

/** Revert to the derived guess (delete the override). */
export function useClearEquipmentOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { esc_counter: string; custno: string }) => {
      const { error } = await supabase
        .from("pegasus_equipment_override").delete().eq("esc_counter", v.esc_counter);
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["equip_overrides", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer_equipment", v.custno] });
    },
  });
}

/**
 * EQUIP-EDIT — the office's Pegasus-native field corrections (manufacturer /
 * model / serial / install date / type / on-plan). Upserts only the provided
 * fields, never touches ESC (DEC-22), and never clobbers an existing removed
 * status. A null value clears that field back to the ESC record.
 */
export function useEditEquipmentFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      esc_counter: string; custno: string; locno?: string | null;
      mfg?: string | null; model?: string | null; serial?: string | null;
      install_date?: string | null; eq_type?: string | null; on_plan?: boolean | null;
      /** ROOM-EDIT-01: move the unit to a different room (null = ESC's room). */
      equip_loc?: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const payload: Record<string, unknown> = {
        esc_counter: v.esc_counter, custno: v.custno, locno: v.locno ?? null,
        set_by: u?.user?.email ?? null, set_at: new Date().toISOString(),
      };
      if (v.mfg !== undefined) payload.mfg = v.mfg;
      if (v.model !== undefined) payload.model = v.model;
      if (v.serial !== undefined) payload.serial = v.serial;
      if (v.install_date !== undefined) payload.install_date = v.install_date;
      if (v.eq_type !== undefined) payload.eq_type = v.eq_type;
      if (v.on_plan !== undefined) payload.on_plan = v.on_plan;
      if (v.equip_loc !== undefined) payload.equip_loc = v.equip_loc;
      const { error } = await supabase
        .from("pegasus_equipment_override").upsert(payload, { onConflict: "esc_counter" });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["equip_overrides", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer_equipment", v.custno] });
    },
  });
}
